import { getCsrfToken } from "../util";

// Every Xendit hand-off from the portal opens in a separate window so the
// user keeps the portal tab. A popup must be opened synchronously inside the
// originating click, otherwise the browser blocks it — so helpers that need an
// async round-trip open a blank popup first, then point it at the URL.

// NOTE: no `noopener` here. With noopener, window.open returns null, so we
// could neither steer the popup to the Xendit URL nor detect a blocked popup
// — and the fallback would navigate the portal tab away. We drop window.opener
// manually after opening to keep the isolation benefit.
const POPUP_FEATURES = "width=520,height=760";

const dropOpener = (win) => {
  try {
    if (win) win.opener = null;
  } catch (_) {
    /* cross-origin after navigation — ignore */
  }
};

export const jsonHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "X-CSRF-Token": getCsrfToken(),
});

// Open a blank popup synchronously inside a click handler. Hand the returned
// handle to payViaPopup so a multi-step flow (e.g. cancel-then-pay) can keep
// the window inside the original user gesture.
export const openBlankPopup = () => {
  try {
    return window.open("", "_blank", POPUP_FEATURES);
  } catch (_) {
    return null;
  }
};

// Open a known Xendit URL in a new window. Falls back to a same-tab
// navigation if the popup is blocked, so the user is never stranded.
export const openXendit = (url) => {
  if (!url) return;
  const win = window.open(url, "_blank", POPUP_FEATURES);
  if (win) dropOpener(win);
  else window.location.href = url;
};

// Synchronously open a blank popup (inside the click), POST for a Xendit URL,
// then steer the popup there. Returns the parsed response so the caller can
// react to { paid } / { error }. On anything but a redirect_url the popup is
// closed so a blank window is never left behind. Pass an existing popup
// (from openBlankPopup) when an earlier await in the same flow already used
// up the user gesture.
export const payViaPopup = async (url, body, existingPopup) => {
  const popup = existingPopup !== undefined ? existingPopup : openBlankPopup();

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));

    if (res.ok && data.redirect_url) {
      if (popup) {
        popup.location.href = data.redirect_url;
        dropOpener(popup);
      } else {
        openXendit(data.redirect_url);
      }
      return { ok: true, ...data };
    }

    if (popup) popup.close();
    return { ok: res.ok, ...data };
  } catch (_) {
    if (popup) popup.close();
    return { ok: false, error: "Gagal memproses pembayaran. Coba lagi." };
  }
};

// POST without a popup (e.g. cancelling a pending payment). Returns parsed JSON.
export const postJson = async (url, body) => {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, ...data };
  } catch (_) {
    return { ok: false, error: "Gagal menghubungi server. Coba lagi." };
  }
};
